import { useCallback, useRef, type ClipboardEvent, type CSSProperties, type MouseEvent } from 'react'
import type { SceneBlock } from '../../lib/types'
import { DEFAULT_SCENE_COLOR, SCENE_COLORS, SCENE_SHADE, sceneInk } from '../../lib/sceneColors'
import { FramedImage } from '../FramedImage'
import { insertPlainText } from './caret'
import { BlockToolbar, ToolbarButton, ToolbarDivider } from './Toolbar'
import type { SelectableProps } from './ThoughtParts'

export { SCENE_COLORS }

/** The custom-colour swatch before a custom colour is chosen. */
const RAINBOW = 'conic-gradient(#D4848B, #E8C27A, #8FB59A, #7A9CC2, #9E6B8A, #D4848B)'
const SWATCH = 'size-[18px] rounded-full border'
const SWATCH_ON = 'border-card ring-2 ring-card/80 ring-offset-1 ring-offset-foreground'

/**
 * A scene (design 4a): a break that runs edge to edge across the page —
 * solid colour or an image, with nothing round it — and one large line set in
 * its middle. Always half height (360px); scenes saved as "full" read the
 * same. An image is framed (crop & position) like every other image; see
 * components/FramedImage.tsx.
 */
export function SceneView({
  block,
  selected,
  onSelect,
  onKeyDown,
  onRemove,
  onChange,
  onPickImage,
  onCrop,
}: Omit<SelectableProps, 'onSelect'> & {
  block: SceneBlock
  /** Select the scene; `focus` moves keyboard focus to it (not when clicking into its text). */
  onSelect: (focus: boolean) => void
  onChange: (patch: Partial<SceneBlock>) => void
  /** Open the image picker for this scene. */
  onPickImage: () => void
  /** Open crop & position for the scene's image. */
  onCrop: () => void
}) {
  const mountedHtml = useRef(block.html)
  const attach = useCallback((el: HTMLElement | null) => {
    if (el && !el.dataset.ready) {
      el.innerHTML = mountedHtml.current
      el.dataset.ready = '1'
    }
  }, [])

  const image = block.mode === 'image'
  const color = block.color || DEFAULT_SCENE_COLOR
  const photo = image ? block.src : undefined
  const ink = image ? 'var(--on-scrim)' : sceneInk(color)
  const isSwatch = SCENE_COLORS.some(([c]) => c.toLowerCase() === color.toLowerCase())

  const onClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    const t = e.target as HTMLElement
    onSelect(!t.closest('input,label,[contenteditable="true"],button'))
  }

  return (
    <section
      data-selectable={block.id}
      tabIndex={0}
      aria-label="Scene"
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{ background: image ? 'var(--scrim)' : color, '--scene-ink': ink } as CSSProperties}
      className="wr-bleed relative clear-both mb-[34px] mt-3.5 flex h-[360px] items-center justify-center overflow-hidden outline-none"
    >
      {photo ? (
        <FramedImage src={photo} frame={block.frame} draggable={false} style={{ position: 'absolute', inset: 0 }} className="pointer-events-none">
          <div aria-hidden className={SCENE_SHADE} />
        </FramedImage>
      ) : (
        image && <div aria-hidden className={SCENE_SHADE} />
      )}
      {/* No ring when selected: nothing may frame a scene (Niki, 10/1). The toolbar shows it's selected. */}
      {image && !block.src && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onPickImage()
          }}
          className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-full border border-on-scrim/40 px-4 py-2 font-sans text-[13px] font-medium text-on-scrim hover:bg-on-scrim/10"
        >
          Add an image
        </button>
      )}

      {selected && (
        <BlockToolbar label="Scene options" className="right-4 top-3.5 margins:right-[calc(50%-340px)]">
          <ToolbarButton pressed={image} onClick={() => (block.src ? onChange({ mode: 'image' }) : onPickImage())}>
            Image
          </ToolbarButton>
          <ToolbarButton pressed={!image} onClick={() => onChange({ mode: 'solid' })}>
            Solid
          </ToolbarButton>
          <ToolbarDivider />
          {image ? (
            <>
              {block.src && <ToolbarButton onClick={onCrop}>Reposition</ToolbarButton>}
              <ToolbarButton onClick={onPickImage}>{block.src ? 'Replace image' : 'Choose image'}</ToolbarButton>
            </>
          ) : (
            <span role="group" aria-label="Scene colour" className="flex items-center gap-1.5 px-1.5">
              {SCENE_COLORS.map(([c, name]) => {
                const on = color.toLowerCase() === c.toLowerCase()
                return (
                  <button
                    key={c}
                    type="button"
                    aria-label={name}
                    title={name}
                    aria-pressed={on}
                    onClick={(e) => {
                      e.stopPropagation()
                      onChange({ color: c, mode: 'solid' })
                    }}
                    style={{ background: c }}
                    className={`${SWATCH} ${on ? SWATCH_ON : 'border-card/30'}`}
                  />
                )
              })}
              {/* Any other colour: the system picker behind a rainbow swatch, filled with the colour once it's custom. */}
              <label
                title="Custom colour"
                onClick={(e) => e.stopPropagation()}
                style={{ background: isSwatch ? RAINBOW : color }}
                className={`relative cursor-pointer ${SWATCH} ${isSwatch ? 'border-card/30' : SWATCH_ON}`}
              >
                <input
                  type="color"
                  aria-label="Custom colour"
                  value={/^#[0-9a-f]{6}$/i.test(color) ? color : DEFAULT_SCENE_COLOR}
                  onChange={(e) => onChange({ color: e.target.value, mode: 'solid' })}
                  className="absolute inset-0 size-full cursor-pointer opacity-0"
                />
              </label>
            </span>
          )}
          <ToolbarDivider />
          {/* One remove control: the word, without the ⌫ glyph beside it (Niki, 10/1). The keys still work. */}
          <ToolbarButton onClick={onRemove} keys="Backspace Delete" title="Remove (Backspace)">
            Remove
          </ToolbarButton>
        </BlockToolbar>
      )}

      <div className="relative flex w-full max-w-[816px] flex-col items-center px-6 py-12 text-center sm:px-10">
        <h2
          ref={attach}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label="Scene line"
          data-scene-line={block.id}
          data-ph="A line to stop on"
          onInput={(e) => {
            const el = e.currentTarget
            if ((el.textContent ?? '') === '' && el.innerHTML !== '') el.innerHTML = ''
            onChange({ html: el.innerHTML })
          }}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') {
              e.preventDefault()
              e.currentTarget.blur()
            }
          }}
          onPaste={(e: ClipboardEvent<HTMLElement>) => {
            e.preventDefault()
            insertPlainText(e.clipboardData.getData('text/plain').replace(/\s+/g, ' '))
            onChange({ html: e.currentTarget.innerHTML })
          }}
          className="wr-prose wr-scene-line m-0 w-full max-w-[680px] font-serif text-[30px]/[1.1] font-normal tracking-[-0.01em] text-[var(--scene-ink)] outline-none [text-wrap:balance] sm:text-[40px]/[1.1]"
        />
      </div>
    </section>
  )
}
